import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { E_GET_PHONE, E_GET_PHONE_CHARGE, emitter } from '../../event';
import iconClose from './assets/icons/close.webp';
import iconContacts from './assets/icons/contacts.webp';
import iconHome from './assets/icons/home.webp';
import iconPhone from './assets/icons/phone.webp';
import iconSettings from './assets/icons/settings.webp';
import iconSms from './assets/icons/sms.webp';
import iconTossup from './assets/icons/tossup.webp';
import bg1 from './assets/bg/1.webp';
import bg2 from './assets/bg/2.webp';
import bg3 from './assets/bg/3.webp';
import bg4 from './assets/bg/4.webp';
import bg5 from './assets/bg/5.webp';
import bg6 from './assets/bg/6.webp';
import bg7 from './assets/bg/7.webp';
import bg8 from './assets/bg/8.webp';
import './style.css';

type PhoneContact = {
    number: number;
    name: string;
    note: string;
};

type PhonePayload = {
    show?: boolean;
    tab?: string;
    number?: number;
    balance?: number;
    enabled?: boolean;
    callPending?: boolean;
    inCall?: boolean;
    callPeer?: number;
    callIncoming?: boolean;
    wallpaperIndex?: number;
    wallpaperUrl?: string;
    contacts?: PhoneContact[];
};

const parsePayload = (payload: any): PhonePayload | null => {
    try {
        const raw = Array.isArray(payload) ? payload[0] : payload;
        if (typeof raw !== 'string') return null;
        return JSON.parse(raw);
    } catch (error) {
        console.error('Phone JSON error', error);
        return null;
    }
};

type PhoneChargePayload = {
    amount?: number;
};

const parseChargePayload = (payload: any): PhoneChargePayload | null => {
    try {
        const raw = Array.isArray(payload) ? payload[0] : payload;
        if (typeof raw !== 'string') return null;
        return JSON.parse(raw);
    } catch (error) {
        console.error('Phone charge JSON error', error);
        return null;
    }
};

const wallpapers = [bg1, bg2, bg3, bg4, bg5, bg6, bg7, bg8];
const DIAL_MAX_LENGTH = 8;
const CONTACT_NAME_MIN_LENGTH = 3;
const CONTACT_NAME_MAX_LENGTH = 24;
const CONTACT_NOTE_MAX_LENGTH = 35;
const CONTACT_NUMBER_MAX_VALUE = 2000000000;
const RECHARGE_MAX_AMOUNT = 300000;
const PHONE_BASE_WIDTH = 470;
const PHONE_BASE_HEIGHT = 920;
const PHONE_MAX_SCALE = 1.3;
const PHONE_MIN_SCALE = 0.62;
const PHONE_REF_W = 3840;
const PHONE_REF_H = 2160;
const PHONE_SAFE_HORIZONTAL = 40;
const PHONE_SAFE_VERTICAL = 36;
const CALL_DIALING_TIMEOUT_MS = 1800;
const CALL_AUTO_HIDE_DELAY_MS = 1300;
const CALL_PENDING_GRACE_MS = 900;
const QUICK_CONTACT_NUMBERS = [
    { number: '02', label: 'Полиция' },
    { number: '03', label: 'Скорая' },
    { number: '090', label: 'Механик' },
    { number: '555', label: 'Такси' },
] as const;
type CallPhase = 'idle' | 'dialing' | 'active';
type RechargePreset = '1000' | '5000' | '10000' | 'custom';
const rechargePresetValues: ReadonlyArray<'1000' | '5000' | '10000'> = ['1000', '5000', '10000'];
const dialPadKeys = [
    { value: '1', letters: '' },
    { value: '2', letters: 'ABC' },
    { value: '3', letters: 'DEF' },
    { value: '4', letters: 'GHI' },
    { value: '5', letters: 'JKL' },
    { value: '6', letters: 'MNO' },
    { value: '7', letters: 'PQRS' },
    { value: '8', letters: 'TUV' },
    { value: '9', letters: 'WXYZ' },
    { value: '*', letters: '' },
    { value: '0', letters: '+' },
    { value: '#', letters: '' },
];

const normalizeDialNumber = (value: string) => value.replace(/[^0-9*#]/g, '').slice(0, DIAL_MAX_LENGTH);
const normalizeContactNumber = (value: string) => value.replace(/[^0-9]/g, '').slice(0, 10);
const normalizeWallpaperIndex = (value: number) => Math.max(0, Math.min(wallpapers.length - 1, Math.floor(value)));
const sanitizeWallpaperUrl = (value: string) => value.trim();
const isValidWallpaperUrl = (value: string) => /^https?:\/\/\S+$/i.test(value) && value.length <= 255;
const canCallNumber = (value: string) => value.length > 0 && value.length <= DIAL_MAX_LENGTH && /^[0-9]+$/.test(value);
const normalizeRechargeAmount = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits.length) return '';
    const amount = Number(digits);
    if (amount > RECHARGE_MAX_AMOUNT) return String(RECHARGE_MAX_AMOUNT);
    return String(amount);
};
const formatCallTime = (seconds: number) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
};

const calculatePhoneScale = () => {
  const vw = Math.max(0, window.innerWidth);
  const vh = Math.max(0, window.innerHeight);

  const rel = Math.min(vw / PHONE_REF_W, vh / PHONE_REF_H);
  let scale = PHONE_MAX_SCALE * rel;

  const fitByWidth = Math.max(0, vw - PHONE_SAFE_HORIZONTAL) / PHONE_BASE_WIDTH;
  const fitByHeight = Math.max(0, vh - PHONE_SAFE_VERTICAL) / PHONE_BASE_HEIGHT;
  scale = Math.min(scale, fitByWidth, fitByHeight, PHONE_MAX_SCALE);

  // 3) clamp
  scale = Math.max(PHONE_MIN_SCALE, scale);

  return Math.round(scale * 1000) / 1000;
};

const Phone = () => {
    const [visible, setVisible] = useState(false);
    const [tab, setTab] = useState('home');
    const [number, setNumber] = useState(0);
    const [balance, setBalance] = useState(0);
    const [enabled, setEnabled] = useState(true);
    const [inCall, setInCall] = useState(false);
    const [contacts, setContacts] = useState<PhoneContact[]>([]);

    const [dialNumber, setDialNumber] = useState('');
    const [smsNumber, setSmsNumber] = useState('');
    const [smsPinnedNumber, setSmsPinnedNumber] = useState('');
    const [smsText, setSmsText] = useState('');
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [rechargePreset, setRechargePreset] = useState<RechargePreset>('1000');
    const [addContactPlayerId, setAddContactPlayerId] = useState('');
    const [editSourceNumber, setEditSourceNumber] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editNumber, setEditNumber] = useState('');
    const [editNote, setEditNote] = useState('');
    const [deleteConfirmNumber, setDeleteConfirmNumber] = useState<number | null>(null);
    const [quickNumbersCollapsed, setQuickNumbersCollapsed] = useState(false);
    const [seededNumbers, setSeededNumbers] = useState(false);
    const [clockLabel, setClockLabel] = useState('');
    const [wallpaperIndex, setWallpaperIndex] = useState(0);
    const [wallpaperUrl, setWallpaperUrl] = useState('');
    const [wallpaperUrlInput, setWallpaperUrlInput] = useState('');
    const [wallpaperError, setWallpaperError] = useState('');
    const [callPhase, setCallPhase] = useState<CallPhase>('idle');
    const [callNumber, setCallNumber] = useState('');
    const [callDuration, setCallDuration] = useState(0);
    const [showEarHint, setShowEarHint] = useState(false);
    const [incomingCall, setIncomingCall] = useState(false);
    const [chargeToast, setChargeToast] = useState('');
    const [phoneScale, setPhoneScale] = useState(() => calculatePhoneScale());
    const inCallRef = useRef(false);
    const incomingCallRef = useRef(false);
    const callUiLockedRef = useRef(false);
    const callAutoHiddenRef = useRef(false);
    const callManualOpenIntentRef = useRef(false);
    const incomingAutoOpenedRef = useRef(false);
    const callPendingRef = useRef(false);
    const callPendingGraceUntilRef = useRef(0);
    const callPhaseRef = useRef<CallPhase>('idle');
    const suppressNextAutoHideRef = useRef(false);
    const callDialTimeoutRef = useRef<number | null>(null);
    const callAutoHideTimeoutRef = useRef<number | null>(null);
    const smsAwaitingChargeRef = useRef(false);
    const smsAwaitingChargeTimerRef = useRef<number | null>(null);
    const closeCooldownUntilRef = useRef(0);

    const sendAction = useCallback((action: string, arg1 = '', arg2 = '') => {
        const a1 = arg1.replace(/\|/g, '/').replace(/\n/g, ' ').trim();
        const a2 = arg2.replace(/\|/g, '/').replace(/\n/g, ' ').trim();
        const payload = [action, a1, a2].filter((part) => part.length > 0).join('|');
        window.cef.emit('cef:phoneAction', payload);
    }, []);

    const openContacts = () => {
        setTab('contacts');
        sendAction('contacts');
    };

    const closePhone = useCallback(() => {
        const keepCallContext = callPendingRef.current || inCallRef.current || callPhaseRef.current !== 'idle';
        closeCooldownUntilRef.current = Date.now() + 220;
        sendAction('close');
        setVisible(false);
        if (!keepCallContext) {
            setEditSourceNumber(null);
            setEditName('');
            setEditNumber('');
            setEditNote('');
            setDeleteConfirmNumber(null);
            setRechargeAmount('');
            setRechargePreset('1000');
            setSmsPinnedNumber('');
            setShowEarHint(false);
            setIncomingCall(false);
            incomingAutoOpenedRef.current = false;
            callAutoHiddenRef.current = false;
            callManualOpenIntentRef.current = false;
            callPendingRef.current = false;
            suppressNextAutoHideRef.current = false;
            incomingCallRef.current = false;
        } else {
            if (callAutoHideTimeoutRef.current !== null) {
                window.clearTimeout(callAutoHideTimeoutRef.current);
                callAutoHideTimeoutRef.current = null;
            }
            callAutoHiddenRef.current = true;
            callManualOpenIntentRef.current = false;
            suppressNextAutoHideRef.current = true;
            incomingAutoOpenedRef.current = false;
        }
        window.cef.set_focus(false);
    }, [sendAction]);

    const beginCall = (targetNumber: string) => {
        const normalized = normalizeDialNumber(targetNumber);
        if (!canCallNumber(normalized)) return;
        callPendingRef.current = true;
        callPendingGraceUntilRef.current = Date.now() + CALL_PENDING_GRACE_MS;
        callUiLockedRef.current = true;
        setIncomingCall(false);
        incomingCallRef.current = false;
        setDialNumber(normalized);
        setCallNumber(normalized);
        setCallDuration(0);
        setCallPhase('dialing');
        setShowEarHint(true);
        setTab('call');
        sendAction('call', normalized);
    };

    const startCall = () => {
        beginCall(dialNumber);
    };

    const hangCall = () => {
        sendAction('hang');
        if (callAutoHideTimeoutRef.current !== null) {
            window.clearTimeout(callAutoHideTimeoutRef.current);
            callAutoHideTimeoutRef.current = null;
        }
        callUiLockedRef.current = false;
        callAutoHiddenRef.current = false;
        callManualOpenIntentRef.current = false;
        callPendingRef.current = false;
        suppressNextAutoHideRef.current = false;
        setInCall(false);
        inCallRef.current = false;
        setCallPhase('idle');
        setCallDuration(0);
        setCallNumber('');
        setShowEarHint(false);
        setIncomingCall(false);
        incomingAutoOpenedRef.current = false;
        incomingCallRef.current = false;
        setTab('home');
    };

    const appendDialKey = (key: string) => {
        setDialNumber((current) => normalizeDialNumber(`${current}${key}`));
    };

    const removeDialKey = () => {
        setDialNumber((current) => current.slice(0, -1));
    };

    const canCallDialNumber = canCallNumber(dialNumber);

    const cancelContactEdit = () => {
        setEditSourceNumber(null);
        setEditName('');
        setEditNumber('');
        setEditNote('');
    };

    const startContactEdit = (contact: PhoneContact) => {
        setDeleteConfirmNumber(null);
        setEditSourceNumber(contact.number);
        setEditName(contact.name || '');
        setEditNumber(String(contact.number));
        setEditNote(contact.note || '');
    };

    const requestDeleteContact = (contact: PhoneContact) => {
        if (deleteConfirmNumber !== contact.number) {
            if (editSourceNumber === contact.number) cancelContactEdit();
            setDeleteConfirmNumber(contact.number);
            return;
        }

        sendAction('contact_delete', String(contact.number));
        setDeleteConfirmNumber(null);
    };

    const cancelDeleteContact = () => {
        setDeleteConfirmNumber(null);
    };

    const selectRechargePreset = (preset: RechargePreset) => {
        setRechargePreset(preset);
        if (preset !== 'custom') setRechargeAmount('');
    };

    const saveContactEdit = () => {
        if (editSourceNumber === null) return;

        const sourceContact = contacts.find((item) => item.number === editSourceNumber);
        if (!sourceContact) {
            cancelContactEdit();
            return;
        }

        const nextName = editName.trim();
        const nextNote = editNote.trim();
        const nextNumberText = normalizeContactNumber(editNumber);
        const nextNumber = Number(nextNumberText);

        if (nextName.length < CONTACT_NAME_MIN_LENGTH || nextName.length > CONTACT_NAME_MAX_LENGTH) return;
        if (nextNote.length < 1 || nextNote.length > CONTACT_NOTE_MAX_LENGTH) return;
        if (!nextNumberText.length || Number.isNaN(nextNumber) || nextNumber < 1 || nextNumber > CONTACT_NUMBER_MAX_VALUE) return;

        const sourceName = (sourceContact.name || '').trim();
        const sourceNote = (sourceContact.note || '').trim();
        if (nextName !== sourceName) {
            sendAction('contact_name', String(editSourceNumber), nextName);
        }
        if (nextNote !== sourceNote) {
            sendAction('contact_note', String(editSourceNumber), nextNote);
        }
        if (nextNumber !== editSourceNumber) {
            sendAction('contact_number', String(editSourceNumber), String(nextNumber));
        }

        cancelContactEdit();
    };

    const selectWallpaper = (index: number) => {
        const nextIndex = normalizeWallpaperIndex(index);
        setWallpaperError('');
        setWallpaperIndex(nextIndex);
        sendAction('wallpaper', String(nextIndex));
        if (wallpaperUrl.length > 0) {
            setWallpaperUrl('');
            setWallpaperUrlInput('');
            sendAction('wallpaper_clear');
        }
    };

    const applyCustomWallpaper = () => {
        const nextUrl = sanitizeWallpaperUrl(wallpaperUrlInput);
        if (!isValidWallpaperUrl(nextUrl)) {
            setWallpaperError('Введите прямую ссылку http/https (до 255 символов)');
            return;
        }

        setWallpaperError('');
        setWallpaperUrl(nextUrl);
        setWallpaperUrlInput(nextUrl);
        sendAction('wallpaper_url', nextUrl);
    };

    const clearCustomWallpaper = () => {
        if (!wallpaperUrl.length && !wallpaperUrlInput.length) return;
        setWallpaperError('');
        setWallpaperUrl('');
        setWallpaperUrlInput('');
        sendAction('wallpaper_clear');
    };

    const clearSmsChargeAwait = () => {
        smsAwaitingChargeRef.current = false;
        if (smsAwaitingChargeTimerRef.current !== null) {
            window.clearTimeout(smsAwaitingChargeTimerRef.current);
            smsAwaitingChargeTimerRef.current = null;
        }
    };

    const armSmsChargeAwait = () => {
        clearSmsChargeAwait();
        smsAwaitingChargeRef.current = true;
        smsAwaitingChargeTimerRef.current = window.setTimeout(() => {
            smsAwaitingChargeRef.current = false;
            smsAwaitingChargeTimerRef.current = null;
        }, 5000);
    };

    const showChargeToastNotice = (amount: number) => {
        if (amount <= 0) return;
        setChargeToast(`Списание ${amount}$`);
        window.setTimeout(() => {
            setChargeToast((current) => (current === `Списание ${amount}$` ? '' : current));
        }, 2100);
    };

    const sendSmsMessage = () => {
        if (!smsNumber.length || !smsText.length) return;
        sendAction('sms', smsNumber, smsText);
        if (!smsPinnedNumber.length || smsPinnedNumber !== smsNumber) {
            setSmsNumber('');
        }
        armSmsChargeAwait();
    };

    useEffect(() => {
        const onPhoneToggleIntent = (event: Event) => {
            const customEvent = event as CustomEvent<string>;
            const action = customEvent.detail;
            if (action !== 'open' && action !== 'close') return;

            if (action === 'open') {
                callManualOpenIntentRef.current = true;
                sendAction('open');
                if (callPendingRef.current || inCallRef.current || callPhaseRef.current !== 'idle' || callAutoHiddenRef.current) {
                    suppressNextAutoHideRef.current = true;
                }
                if (callAutoHiddenRef.current && inCallRef.current) {
                    callAutoHiddenRef.current = false;
                    setVisible(true);
                    setTab('call');
                    window.cef.set_focus(true);
                }
                return;
            }

            if (visible) {
                closePhone();
                return;
            }
            sendAction('close');
            window.cef.set_focus(false);
            callManualOpenIntentRef.current = false;
        };

        window.addEventListener('ui:phoneToggleIntent', onPhoneToggleIntent as EventListener);

        const unsubscribe = emitter.subscribe(E_GET_PHONE, (payload: any) => {
            const result = parsePayload(payload);
            if (!result) return;

            if (result.show === false) {
                const keepCallContext = callPendingRef.current || inCallRef.current || callPhaseRef.current !== 'idle';
                setVisible(false);
                window.cef.set_focus(false);
                if (!keepCallContext) {
                    setSeededNumbers(false);
                    setRechargeAmount('');
                    setRechargePreset('1000');
                    setSmsPinnedNumber('');
                    setAddContactPlayerId('');
                    callUiLockedRef.current = false;
                    setCallPhase('idle');
                    setCallDuration(0);
                    setCallNumber('');
                    setShowEarHint(false);
                    setIncomingCall(false);
                    incomingAutoOpenedRef.current = false;
                    inCallRef.current = false;
                    incomingCallRef.current = false;
                    callAutoHiddenRef.current = false;
                    callManualOpenIntentRef.current = false;
                    callPendingRef.current = false;
                    suppressNextAutoHideRef.current = false;
                    if (callAutoHideTimeoutRef.current !== null) {
                        window.clearTimeout(callAutoHideTimeoutRef.current);
                        callAutoHideTimeoutRef.current = null;
                    }
                    cancelContactEdit();
                    setDeleteConfirmNumber(null);
                } else {
                    if (callAutoHideTimeoutRef.current !== null) {
                        window.clearTimeout(callAutoHideTimeoutRef.current);
                        callAutoHideTimeoutRef.current = null;
                    }
                    callAutoHiddenRef.current = true;
                    callManualOpenIntentRef.current = false;
                    suppressNextAutoHideRef.current = true;
                    incomingAutoOpenedRef.current = false;
                }
                return;
            }

            const payloadInCall = typeof result.inCall === 'boolean' ? result.inCall : inCallRef.current;
            const payloadCallPending = typeof result.callPending === 'boolean' ? result.callPending : callPendingRef.current;
            const payloadIncoming = typeof result.callIncoming === 'boolean' ? result.callIncoming : incomingCallRef.current;
            const payloadCallPeer = typeof result.callPeer === 'number' ? result.callPeer : 0;
            const isDialingGrace = callPhaseRef.current === 'dialing' && Date.now() < callPendingGraceUntilRef.current;

            if (!payloadCallPending && !payloadInCall) {
                if (isDialingGrace) {
                    callPendingRef.current = true;
                } else {
                    callPendingRef.current = false;
                    callUiLockedRef.current = false;
                    setShowEarHint(false);
                    setIncomingCall(false);
                    incomingCallRef.current = false;
                    setCallPhase('idle');
                    setCallDuration(0);
                    setCallNumber('');
                }
            } else {
                callPendingRef.current = payloadCallPending;
            }

            setIncomingCall(payloadIncoming);
            incomingCallRef.current = payloadIncoming;
            if (payloadIncoming && !payloadInCall && !visible) {
                incomingAutoOpenedRef.current = true;
            }

            if (payloadCallPeer > 0) {
                setCallNumber(String(payloadCallPeer));
            }

            if (!payloadCallPending && !payloadInCall && incomingAutoOpenedRef.current) {
                incomingAutoOpenedRef.current = false;
                callManualOpenIntentRef.current = false;
                suppressNextAutoHideRef.current = false;
                setTab('home');
                setVisible(false);
                window.cef.set_focus(false);
                return;
            }

            const shouldStayHidden =
                callAutoHiddenRef.current &&
                !visible &&
                !callManualOpenIntentRef.current;

            if (
                !visible &&
                Date.now() < closeCooldownUntilRef.current &&
                !payloadCallPending &&
                !payloadInCall &&
                !callManualOpenIntentRef.current
            ) {
                setVisible(false);
                window.cef.set_focus(false);
                return;
            }

            if (shouldStayHidden) {
                if (!payloadCallPending) callAutoHiddenRef.current = false;
                setVisible(false);
                window.cef.set_focus(false);
            } else {
                setVisible(true);
                const keepLookFree = payloadIncoming && !payloadInCall;
                window.cef.set_focus(!keepLookFree);
                if (callManualOpenIntentRef.current) {
                    callManualOpenIntentRef.current = false;
                    callAutoHiddenRef.current = false;
                }
            }

            if (typeof result.tab === 'string' && result.tab.length > 0) {
                const nextTab = result.tab;
                setTab((current) => {
                    if (callUiLockedRef.current && nextTab !== 'call') return current;
                    return nextTab;
                });
            }
            if (typeof result.number === 'number') {
                setNumber(result.number);
                if (!seededNumbers) {
                    setDialNumber('');
                    setSmsNumber('');
                    setSmsPinnedNumber('');
                    setSeededNumbers(true);
                }
            }
            if (typeof result.balance === 'number') setBalance(result.balance);
            if (typeof result.enabled === 'boolean') setEnabled(result.enabled);
            if (typeof result.inCall === 'boolean') setInCall(result.inCall);
            if (typeof result.wallpaperIndex === 'number') {
                setWallpaperIndex(normalizeWallpaperIndex(result.wallpaperIndex));
            }
            if (typeof result.wallpaperUrl === 'string') {
                const nextUrl = sanitizeWallpaperUrl(result.wallpaperUrl);
                setWallpaperUrl(nextUrl);
                setWallpaperUrlInput(nextUrl);
                if (!nextUrl.length) setWallpaperError('');
            }
            if (Array.isArray(result.contacts)) {
                setContacts(result.contacts);
                setDeleteConfirmNumber((current) =>
                    current !== null && result.contacts?.some((contact) => contact.number === current) ? current : null
                );
            }
        });

        const unsubscribeCharge = emitter.subscribe(E_GET_PHONE_CHARGE, (payload: any) => {
            const result = parseChargePayload(payload);
            if (!result || typeof result.amount !== 'number' || result.amount <= 0) return;
            if (!visible) return;
            showChargeToastNotice(result.amount);
            if (smsAwaitingChargeRef.current && (result.amount === 5 || result.amount === 25)) {
                setSmsText('');
                clearSmsChargeAwait();
            }
        });

        const onKeyDown = (event: KeyboardEvent) => {
            if (!visible) return;
            if (event.keyCode === 27) {
                event.preventDefault();
                closePhone();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('ui:phoneToggleIntent', onPhoneToggleIntent as EventListener);
            unsubscribe();
            unsubscribeCharge();
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [visible, seededNumbers, closePhone, sendAction]);

    useEffect(() => {
        callPhaseRef.current = callPhase;
    }, [callPhase]);

    useEffect(() => {
        if (inCall && !inCallRef.current) {
            if (callAutoHideTimeoutRef.current !== null) {
                window.clearTimeout(callAutoHideTimeoutRef.current);
                callAutoHideTimeoutRef.current = null;
            }
            callPendingRef.current = true;
            setIncomingCall(false);
            incomingAutoOpenedRef.current = false;
            incomingCallRef.current = false;
            callUiLockedRef.current = true;
            setCallPhase('active');
            setCallDuration(0);
            if (!callNumber.length && dialNumber.length) setCallNumber(dialNumber);
            setTab('call');
            if (visible) {
                setShowEarHint(true);
                if (suppressNextAutoHideRef.current) {
                    suppressNextAutoHideRef.current = false;
                } else {
                    callAutoHideTimeoutRef.current = window.setTimeout(() => {
                        if (!inCallRef.current) return;
                        callAutoHiddenRef.current = true;
                        callManualOpenIntentRef.current = false;
                        setVisible(false);
                        setShowEarHint(false);
                        window.cef.set_focus(false);
                        callAutoHideTimeoutRef.current = null;
                    }, CALL_AUTO_HIDE_DELAY_MS);
                }
            } else {
                setShowEarHint(false);
            }
        }

        if (!inCall && inCallRef.current) {
            if (callAutoHideTimeoutRef.current !== null) {
                window.clearTimeout(callAutoHideTimeoutRef.current);
                callAutoHideTimeoutRef.current = null;
            }
            callUiLockedRef.current = false;
            setCallPhase('idle');
            setCallDuration(0);
            setCallNumber('');
            setShowEarHint(false);
            setIncomingCall(false);
            incomingAutoOpenedRef.current = false;
            incomingCallRef.current = false;
            callAutoHiddenRef.current = false;
            callManualOpenIntentRef.current = false;
            suppressNextAutoHideRef.current = false;
            setTab((current) => (current === 'call' ? 'home' : current));
        }

        inCallRef.current = inCall;
    }, [inCall, callNumber.length, dialNumber, visible]);

    useEffect(
        () => () => {
            if (callAutoHideTimeoutRef.current !== null) {
                window.clearTimeout(callAutoHideTimeoutRef.current);
                callAutoHideTimeoutRef.current = null;
            }
            if (smsAwaitingChargeTimerRef.current !== null) {
                window.clearTimeout(smsAwaitingChargeTimerRef.current);
                smsAwaitingChargeTimerRef.current = null;
            }
        },
        []
    );

    useEffect(() => {
        if (callDialTimeoutRef.current !== null) {
            window.clearTimeout(callDialTimeoutRef.current);
            callDialTimeoutRef.current = null;
        }

        if (!visible || callPhase !== 'dialing') return;

        callDialTimeoutRef.current = window.setTimeout(() => {
            if (inCallRef.current || callPendingRef.current) return;
            callUiLockedRef.current = false;
            setCallPhase('idle');
            setCallDuration(0);
            setCallNumber('');
            setTab('home');
        }, CALL_DIALING_TIMEOUT_MS);

        return () => {
            if (callDialTimeoutRef.current !== null) {
                window.clearTimeout(callDialTimeoutRef.current);
                callDialTimeoutRef.current = null;
            }
        };
    }, [callPhase, visible]);

    useEffect(() => {
        if (callPhase !== 'active') return;

        const timer = window.setInterval(() => {
            setCallDuration((seconds) => seconds + 1);
        }, 1000);

        return () => window.clearInterval(timer);
    }, [callPhase]);

    useEffect(() => {
        const syncClock = () => {
            const now = new Date();
            setClockLabel(
                now.toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                })
            );
        };

        syncClock();
        const timer = setInterval(syncClock, 30000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const syncScale = () => {
            setPhoneScale(calculatePhoneScale());
        };

        syncScale();
        window.addEventListener('resize', syncScale);
        return () => window.removeEventListener('resize', syncScale);
    }, []);

    const wrapStyle = useMemo(
        () => ({ ['--phone-scale' as any]: phoneScale }),
        [phoneScale]
    );

    const screenStyle = useMemo(() => {
        const wallpaper = wallpaperUrl.length > 0 ? wallpaperUrl : wallpapers[wallpaperIndex];
        return {
            backgroundImage: `linear-gradient(180deg, rgba(5, 12, 22, 0.78), rgba(5, 12, 22, 0.62)), url(${wallpaper})`,
        };
    }, [wallpaperIndex, wallpaperUrl]);

    if (!visible) return null;

    return (
        <div className="cef-phone-wrap" style={wrapStyle}>
            <div className="cef-phone-shell">
                <div className="cef-phone-topline">
                    <span className="cef-phone-topline-time">{clockLabel || '--:--'}</span>
                    <span className="cef-phone-topline-number">#{number}</span>
                </div>
                <div className="cef-phone-island">
                    <span className={`cef-phone-island-dot ${enabled ? 'online' : 'offline'}`} />
                    <span>ADV</span>
                </div>

                <div className="cef-phone-screen" style={screenStyle}>
                    <div className="cef-phone-ui">
                        {chargeToast.length > 0 && <div className="cef-phone-inline-notice">{chargeToast}</div>}

                        {tab === 'home' && (
                            <div className="cef-phone-home-body">
                                <div className="cef-phone-overview">
                                    <div className="cef-phone-overview-balance">
                                        <span>Баланс</span>
                                        <strong>{balance}$</strong>
                                    </div>
                                    <div className="cef-phone-overview-text">
                                        <span>{enabled ? 'Телефон включён' : 'Телефон отключен'}</span>
                                        <span>{inCall ? 'Идёт звонок' : 'Ожидание действий'}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`cef-phone-power-toggle ${enabled ? 'on' : 'off'}`}
                                        onClick={() => sendAction('toggle')}
                                        aria-label={enabled ? 'Выключить телефон' : 'Включить телефон'}
                                    >
                                        <span className="cef-phone-power-toggle-track">{enabled ? 'ON' : 'OFF'}</span>
                                        <span className="cef-phone-power-toggle-thumb" />
                                    </button>
                                </div>

                                <div className="cef-phone-app-grid">
                                    <button className="cef-phone-app" onClick={openContacts}>
                                        <span className="cef-phone-app-icon">
                                            <img src={iconContacts} alt="Контакты" />
                                        </span>
                                        <span className="cef-phone-app-label">Контакты</span>
                                    </button>

                                    <button className="cef-phone-app" onClick={() => setTab('recharge')}>
                                        <span className="cef-phone-app-icon">
                                            <img src={iconTossup} alt="Пополнить" />
                                        </span>
                                        <span className="cef-phone-app-label">Пополнить</span>
                                    </button>

                                    <button className="cef-phone-app" onClick={() => setTab('settings')}>
                                        <span className="cef-phone-app-icon">
                                            <img src={iconSettings} alt="Настройки" />
                                        </span>
                                        <span className="cef-phone-app-label">Настройки</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {tab === 'dialer' && (
                            <div className="cef-phone-tab">
                                <div className="cef-phone-app-head">
                                    <h3>Набор номера</h3>
                                </div>
                                <div className="cef-phone-dialer">
                                    <div className="cef-phone-dialer-display">
                                        <input
                                            className="cef-phone-dialer-input"
                                            value={dialNumber}
                                            onChange={(event) => setDialNumber(normalizeDialNumber(event.target.value))}
                                            placeholder="Номер"
                                            maxLength={DIAL_MAX_LENGTH}
                                            inputMode="numeric"
                                        />
                                        <button
                                            type="button"
                                            className="cef-phone-dialer-clear"
                                            onClick={removeDialKey}
                                            disabled={!dialNumber.length}
                                            aria-label="Удалить символ"
                                        >
                                            &#9003;
                                        </button>
                                    </div>

                                    <div className="cef-phone-dialpad">
                                        {dialPadKeys.map((key) => (
                                            <button
                                                key={key.value}
                                                type="button"
                                                className="cef-phone-dial-key"
                                                onClick={() => appendDialKey(key.value)}
                                                aria-label={`Клавиша ${key.value}`}
                                            >
                                                <span className="digit">{key.value}</span>
                                                <span className="letters">{key.letters || '\u00a0'}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className="cef-phone-dial-call"
                                        disabled={!canCallDialNumber}
                                        onClick={startCall}
                                    >
                                        <img src={iconPhone} alt="" />
                                        <span>Позвонить</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {tab === 'call' && (
                            <div className="cef-phone-call">
                                <div className="cef-phone-call-avatar">
                                    <img src={iconPhone} alt="Call" />
                                </div>
                                <span className={`cef-phone-call-state ${callPhase === 'dialing' ? 'dialing' : ''}`}>
                                    {incomingCall ? 'Входящий звонок' : callPhase === 'dialing' ? 'Вызов' : 'Идет звонок'}
                                </span>
                                <h3>{callNumber.length ? `#${callNumber}` : 'Неизвестный номер'}</h3>
                                <p>{callPhase === 'active' ? formatCallTime(callDuration) : incomingCall ? 'Ожидание ответа...' : 'Соединение...'}</p>
                                {incomingCall && <div className="cef-phone-call-tip incoming">Нажмите P, чтобы принять • H, чтобы сбросить</div>}
                                {!incomingCall && showEarHint && <div className="cef-phone-call-tip">Поднесите телефон к уху на P</div>}
                                {!incomingCall && (
                                    <button type="button" className="cef-phone-call-hang" onClick={hangCall}>
                                        <img src={iconClose} alt="" />
                                        <span>Сбросить</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {tab === 'sms' && (
                            <div className="cef-phone-tab">
                                <div className="cef-phone-app-head">
                                    <h3>Сообщение</h3>
                                </div>
                                <input
                                    className="cef-phone-sms-number"
                                    value={smsNumber}
                                    onChange={(event) => {
                                        const nextNumber = event.target.value.replace(/[^0-9]/g, '');
                                        setSmsNumber(nextNumber);
                                        if (smsPinnedNumber.length && nextNumber !== smsPinnedNumber) {
                                            setSmsPinnedNumber('');
                                        }
                                    }}
                                    placeholder="Номер телефона"
                                    maxLength={8}
                                />
                                <textarea
                                    className="cef-phone-sms-text"
                                    value={smsText}
                                    onChange={(event) => setSmsText(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
                                        event.preventDefault();
                                        sendSmsMessage();
                                    }}
                                    placeholder="Текст сообщения"
                                    maxLength={65}
                                />
                                <button className="cef-phone-btn primary" onClick={sendSmsMessage}>
                                    Отправить
                                </button>
                            </div>
                        )}

                        {tab === 'recharge' && (
                            <div className="cef-phone-tab">
                                <div className="cef-phone-app-head">
                                    <h3>Пополнение</h3>
                                </div>
                                <div className="cef-phone-recharge-presets">
                                    {rechargePresetValues.map((value) => (
                                        <button
                                            key={`recharge-${value}`}
                                            type="button"
                                            className={`cef-phone-mini-btn ${rechargePreset === value ? 'active' : ''}`}
                                            onClick={() => selectRechargePreset(value)}
                                        >
                                            {value}$
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className={`cef-phone-mini-btn ${rechargePreset === 'custom' ? 'active' : ''}`}
                                        onClick={() => selectRechargePreset('custom')}
                                    >
                                        Своя сумма
                                    </button>
                                </div>
                                {rechargePreset === 'custom' && (
                                    <input
                                        value={rechargeAmount}
                                        onChange={(event) => setRechargeAmount(normalizeRechargeAmount(event.target.value))}
                                        placeholder="Сумма (1-300000)"
                                        maxLength={6}
                                    />
                                )}
                                <button
                                    className="cef-phone-btn primary"
                                    onClick={() => {
                                        const amount =
                                            rechargePreset === 'custom'
                                                ? normalizeRechargeAmount(rechargeAmount)
                                                : rechargePreset;
                                        if (!amount.length) return;
                                        sendAction('recharge', amount);
                                        if (rechargePreset === 'custom') setRechargeAmount('');
                                    }}
                                >
                                    {`Пополнить на ${rechargePreset === 'custom' ? (rechargeAmount.length ? rechargeAmount : '...') : rechargePreset}$`}
                                </button>
                            </div>
                        )}

                        {tab === 'contacts' && (
                            <div className="cef-phone-tab">
                                <div className="cef-phone-app-head">
                                    <h3>Контакты</h3>
                                </div>

                                <div className={`cef-phone-quick ${quickNumbersCollapsed ? 'collapsed' : ''}`}>
                                    <button
                                        type="button"
                                        className="cef-phone-quick-toggle"
                                        onClick={() => setQuickNumbersCollapsed((current) => !current)}
                                    >
                                        <span>Быстрые номера</span>
                                        <span>{quickNumbersCollapsed ? 'Развернуть' : 'Свернуть'}</span>
                                    </button>

                                    {!quickNumbersCollapsed && (
                                        <div className="cef-phone-quick-grid">
                                            {QUICK_CONTACT_NUMBERS.map((item) => (
                                                <button
                                                    key={`quick-${item.number}`}
                                                    type="button"
                                                    className="cef-phone-mini-btn cef-phone-quick-item"
                                                    onClick={() => beginCall(item.number)}
                                                >
                                                    <span className="number">{item.number}</span>
                                                    <span className="label">{item.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="cef-phone-contact-add">
                                    <input
                                        value={addContactPlayerId}
                                        onChange={(event) => setAddContactPlayerId(event.target.value.replace(/[^0-9]/g, ''))}
                                        placeholder="ID игрока для /add"
                                        maxLength={4}
                                    />
                                    <button
                                        className="cef-phone-mini-btn"
                                        onClick={() => {
                                            if (!addContactPlayerId.length) return;
                                            sendAction('add', addContactPlayerId);
                                            setAddContactPlayerId('');
                                        }}
                                    >
                                        Добавить
                                    </button>
                                </div>

                                <div className="cef-phone-contacts">
                                    {contacts.length === 0 && <div className="empty">Контактов пока нет</div>}
                                    {contacts.map((contact, index) => {
                                        const isEditing = editSourceNumber === contact.number;
                                        const isDeleting = deleteConfirmNumber === contact.number;

                                        return (
                                            <div className={`cef-phone-contact ${isEditing ? 'editing' : ''}`} key={`${contact.number}-${index}`}>
                                                <div className="cef-phone-contact-main">
                                                    {!isEditing && (
                                                        <>
                                                            <h4>{contact.name || `Контакт ${contact.number}`}</h4>
                                                            <p>{`#${contact.number}${contact.note ? ` • ${contact.note}` : ''}`}</p>
                                                        </>
                                                    )}

                                                    {isEditing && (
                                                        <div className="cef-phone-contact-edit">
                                                            <input
                                                                value={editName}
                                                                onChange={(event) => setEditName(event.target.value)}
                                                                placeholder="Имя контакта"
                                                                maxLength={CONTACT_NAME_MAX_LENGTH}
                                                            />
                                                            <input
                                                                value={editNumber}
                                                                onChange={(event) => setEditNumber(normalizeContactNumber(event.target.value))}
                                                                placeholder="Номер контакта"
                                                                maxLength={10}
                                                            />
                                                            <input
                                                                value={editNote}
                                                                onChange={(event) => setEditNote(event.target.value)}
                                                                placeholder="Примечание"
                                                                maxLength={CONTACT_NOTE_MAX_LENGTH}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="cef-phone-contact-actions">
                                                    {!isEditing && !isDeleting && (
                                                        <>
                                                            <button
                                                                className="cef-phone-mini-btn"
                                                                onClick={() => {
                                                                    setDialNumber(String(contact.number));
                                                                    setTab('dialer');
                                                                }}
                                                            >
                                                                Звонок
                                                            </button>
                                                            <button
                                                                className="cef-phone-mini-btn"
                                                                onClick={() => {
                                                                    setSmsNumber(String(contact.number));
                                                                    setSmsPinnedNumber(String(contact.number));
                                                                    setTab('sms');
                                                                }}
                                                            >
                                                                SMS
                                                            </button>
                                                            <button className="cef-phone-mini-btn" onClick={() => startContactEdit(contact)}>
                                                                Изм.
                                                            </button>
                                                            <button className="cef-phone-mini-btn danger" onClick={() => requestDeleteContact(contact)}>
                                                                Удалить
                                                            </button>
                                                        </>
                                                    )}

                                                    {!isEditing && isDeleting && (
                                                        <>
                                                            <button className="cef-phone-mini-btn danger" onClick={() => requestDeleteContact(contact)}>
                                                                Подтв. удаление
                                                            </button>
                                                            <button className="cef-phone-mini-btn" onClick={cancelDeleteContact}>
                                                                Отмена
                                                            </button>
                                                        </>
                                                    )}

                                                    {isEditing && (
                                                        <>
                                                            <button className="cef-phone-mini-btn" onClick={saveContactEdit}>
                                                                Сохранить
                                                            </button>
                                                            <button className="cef-phone-mini-btn" onClick={cancelContactEdit}>
                                                                Отмена
                                                            </button>
                                                            <button className="cef-phone-mini-btn danger" onClick={() => requestDeleteContact(contact)}>
                                                                Удалить
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {tab === 'settings' && (
                            <div className="cef-phone-tab">
                                <div className="cef-phone-app-head">
                                    <h3>Настройки</h3>
                                </div>

                                <p className="cef-phone-settings-hint">Прямая ссылка на картинку</p>
                                <div className="cef-phone-wallpaper-url">
                                    <input
                                        value={wallpaperUrlInput}
                                        onChange={(event) => {
                                            setWallpaperUrlInput(event.target.value);
                                            if (wallpaperError.length) setWallpaperError('');
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key !== 'Enter') return;
                                            event.preventDefault();
                                            applyCustomWallpaper();
                                        }}
                                        placeholder="https://site.com/image.jpg"
                                        maxLength={255}
                                    />
                                    <div className="cef-phone-wallpaper-actions">
                                        <button className="cef-phone-mini-btn" onClick={applyCustomWallpaper}>
                                            Применить URL
                                        </button>
                                        <button className="cef-phone-mini-btn" onClick={clearCustomWallpaper} disabled={!wallpaperUrl.length}>
                                            Сбросить URL
                                        </button>
                                    </div>
                                    {wallpaperError.length > 0 && <p className="cef-phone-settings-error">{wallpaperError}</p>}
                                    {wallpaperUrl.length > 0 && <p className="cef-phone-settings-active">Активен пользовательский фон</p>}
                                </div>

                                <p className="cef-phone-settings-hint">Стандартные обои</p>
                                <div className="cef-phone-wall-grid">
                                    {wallpapers.map((bg, index) => (
                                        <button
                                            key={`phone-bg-${index}`}
                                            className={`cef-phone-wall-item ${!wallpaperUrl.length && wallpaperIndex === index ? 'active' : ''}`}
                                            onClick={() => selectWallpaper(index)}
                                        >
                                            <img src={bg} alt={`Обои ${index + 1}`} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {tab !== 'call' && (
                            <div className="cef-phone-dock">
                                <button className={tab === 'home' ? 'active' : ''} onClick={() => setTab('home')}>
                                    <img src={iconHome} alt="Домой" />
                                    <span>Домой</span>
                                </button>
                                <button className={tab === 'dialer' ? 'active' : ''} onClick={() => setTab('dialer')}>
                                    <img src={iconPhone} alt="Звонки" />
                                    <span>Звонки</span>
                                </button>
                                <button className={tab === 'sms' ? 'active' : ''} onClick={() => setTab('sms')}>
                                    <img src={iconSms} alt="SMS" />
                                    <span>SMS</span>
                                </button>
                                <button className="danger" onClick={closePhone}>
                                    <img src={iconClose} alt="Закрыть" />
                                    <span>Закрыть</span>
                                </button>
                            </div>
                        )}

                        {tab !== 'call' && <div className="cef-phone-home-indicator" />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Phone;
